import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { Button, Tooltip } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  redo,
  selectCanRedo,
  selectCanUndo,
  undo,
} from "../../app/slices/HistorySlice";
import useShortcut, { ShortCuts } from "../../hooks/UseShortcutsHook";
import { flushPendingTextEdits } from "../../utils/pendingTextEdits";
import { CopyPasteContext } from "../training_timeline/CopyPasteContext";

const BUTTON_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontFamily: "Poppins",
  fontSize: "13px",
  lineHeight: "18px",
  height: "100%",
  minWidth: 0,
  padding: "4px 12px",
  borderRadius: 0,
  border: "none",
  color: "var(--pi-key-blue)",
  background: "transparent",
  transition: "background 200ms ease, color 200ms ease",
  "&:hover": {
    background: "color-mix(in srgb, var(--pi-key-blue) 10%, transparent)",
    color: "var(--pi-key-blue)",
  },
  "&.Mui-disabled": {
    color: "var(--pi-key-blue)",
    opacity: 0.45,
    background: "transparent",
  },
} as const;

export default function UndoRedoGroup() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const canUndo = useSelector(selectCanUndo);
  const canRedo = useSelector(selectCanRedo);
  const copyPasteContext = useContext(CopyPasteContext);

  const handleUndo = () => {
    copyPasteContext?.clearPendingCut?.();
    flushPendingTextEdits();
    dispatch(undo());
  };

  const handleRedo = () => {
    copyPasteContext?.clearPendingCut?.();
    flushPendingTextEdits();
    dispatch(redo());
  };

  useShortcut(ShortCuts.Undo, handleUndo);
  useShortcut(ShortCuts.Redo, handleRedo);

  return (
    <div className="trainingactionbar-undoredo">
      <Tooltip title={`${t("undo")} (Ctrl+Z)`}>
        <span>
          <Button
            size="small"
            className="trainingactionbar-undoredo-button"
            onClick={handleUndo}
            disabled={!canUndo}
            aria-label={String(t("undo"))}
            startIcon={<UndoOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={BUTTON_SX}
          >
            {t("undo")}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={`${t("redo")} (Ctrl+Shift+Z)`}>
        <span>
          <Button
            size="small"
            className="trainingactionbar-undoredo-button"
            onClick={handleRedo}
            disabled={!canRedo}
            aria-label={String(t("redo"))}
            endIcon={<RedoOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={BUTTON_SX}
          >
            {t("redo")}
          </Button>
        </span>
      </Tooltip>
    </div>
  );
}
